/**
 * 성별·연령대 분포 응답을 '명 수'와 '비율'로 가른다.
 *
 * 서버가 같은 자리에 비율(%)을 넣어 보내기도 하고 집계된 명 수를 넣어 보내기도 한다
 * (fullspec의 예시는 합이 100인 비율이다). 둘을 구분하지 않으면 "42%"를 "42명"으로
 * 읽거나 그 반대가 된다.
 *
 * 비율만 온 경우 명 수는 모른다고 답한다(null). 총원으로 역산하면 반올림 오차가 섞인
 * '사실처럼 보이는 추정치'가 되고, 어드민이 그 숫자를 보고 판단하게 된다.
 *
 * @param {object} raw    서버 응답의 분포 객체
 * @param {Array<{key: string, label: string}>} fields  읽을 항목과 화면 이름
 * @param {number} [total]  서버가 알려준 분모. 성별은 전체 배우, 연령대는 생년월일이
 *   입력된 배우만 세므로 둘이 다르다. 항목 합으로 나누면 비율이 틀어진다.
 * @returns {Array<{label: string, count: number|null, pct: number}>}
 */
export function readDistribution(raw, fields, total) {
  const src = raw ?? {};

  const entries = fields.map(({ key, label }) => {
    const value = src[key];

    // { male: { count: 42, ratio: 38.2 } } 형태
    if (value && typeof value === 'object') {
      const count = numberOrNull(value.count ?? value.total);
      const pct = numberOrNull(value.ratio ?? value.percent ?? value.percentage);
      return { label, count, pct };
    }

    // { male: 42, maleCount: 120 } 형태 — 명 수가 따로 오면 그쪽이 사실이다
    return { label, count: numberOrNull(src[`${key}Count`]), pct: null, plain: numberOrNull(value) };
  });

  const counts = entries.map(e => e.count).filter(n => n != null);
  if (counts.length > 0) return withPct(entries.map(e => ({ ...e, count: e.count ?? 0 })), total);

  // 명 수가 하나도 없다 — 남은 숫자가 비율인지 명 수인지 합으로 가린다
  const plains = entries.map(e => e.pct ?? e.plain ?? 0);
  const sum = plains.reduce((a, b) => a + b, 0);

  if (isPercentLike(plains, sum)) {
    return entries.map((e, i) => ({ label: e.label, count: null, pct: round1(plains[i]) }));
  }
  return withPct(entries.map((e, i) => ({ label: e.label, count: plains[i] })), total);
}

/** 합이 100 근처이고 어느 값도 100을 넘지 않으면 비율로 본다. 명 수와 구분되지 않는 경우는 비율로 둔다. */
function isPercentLike(values, sum) {
  if (sum === 0) return true;
  return sum >= 98 && sum <= 102 && values.every(v => v >= 0 && v <= 100);
}

/** 분모는 서버가 알려준 값을 먼저 쓴다. 없을 때만 항목 합으로 나눈다. */
function withPct(entries, total) {
  const denom = (typeof total === 'number' && total > 0)
    ? total
    : entries.reduce((a, e) => a + e.count, 0);
  return entries.map(e => ({
    label: e.label,
    count: e.count,
    pct: denom > 0 ? round1((e.count / denom) * 100) : 0,
  }));
}

function numberOrNull(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

/** 분포 전체 인원. 비율만 와서 모르면 null */
export function distributionTotal(rows) {
  if (rows.some(r => r.count == null)) return null;
  return rows.reduce((a, r) => a + r.count, 0);
}
